import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Store, Users, TrendingUp, HeartHandshake, CreditCard, MapPin, CheckCircle2, ArrowRight, X, ChevronDown, Star, Gift, BarChart3, Sparkles } from 'lucide-react';
import { useT } from '../components/site/i18n';
import { PhoneImage } from '../components/site/PhoneImage';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-841" data-mp="ns123z1anu0l">
    {children}
  </motion.div>;
export function ParaNegocio() {
  const t = useT();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const plans = [{
    name: t.precos.plan1Name,
    desc: t.precos.plan1Desc,
    priceM: t.precos.plan1PriceM,
    priceA: t.precos.plan1PriceA,
    cta: t.precos.plan1Cta,
    advantage: t.precos.plan1Advantage,
    features: [{
      text: t.precos.feature1,
      included: true
    }, {
      text: t.precos.feature2,
      included: true
    }, {
      text: t.precos.feature3,
      included: true
    }, {
      text: t.precos.feature4,
      included: true
    }, {
      text: t.precos.feature5,
      included: false
    }, {
      text: t.precos.feature6,
      included: false
    }, {
      text: t.precos.feature7,
      included: false
    }, {
      text: t.precos.feature8,
      included: false
    }, {
      text: t.precos.feature9,
      included: false
    }, {
      text: t.precos.feature10,
      included: false
    }],
    popular: false
  }, {
    name: t.precos.plan2Name,
    desc: t.precos.plan2Desc,
    priceM: t.precos.plan2PriceM,
    priceA: t.precos.plan2PriceA,
    cta: t.precos.plan2Cta,
    advantage: t.precos.plan2Advantage,
    badge: t.precos.plan2Badge,
    features: [{
      text: t.precos.feature1,
      included: true
    }, {
      text: t.precos.feature2,
      included: true
    }, {
      text: t.precos.feature3,
      included: true
    }, {
      text: t.precos.feature4,
      included: true
    }, {
      text: t.precos.feature5,
      included: true
    }, {
      text: t.precos.feature6,
      included: true
    }, {
      text: t.precos.feature7,
      included: true,
      detail: '(1 usuário)'
    }, {
      text: t.precos.feature8,
      included: true,
      detail: 'Básico'
    }, {
      text: t.precos.feature9,
      included: false
    }, {
      text: t.precos.feature10,
      included: false
    }],
    popular: true
  }, {
    name: t.precos.plan3Name,
    desc: t.precos.plan3Desc,
    priceM: t.precos.plan3PriceM,
    priceA: t.precos.plan3PriceA,
    cta: t.precos.plan3Cta,
    advantage: t.precos.plan3Advantage,
    features: [{
      text: t.precos.feature1,
      included: true
    }, {
      text: t.precos.feature2,
      included: true
    }, {
      text: t.precos.feature3,
      included: true
    }, {
      text: t.precos.feature4,
      included: true
    }, {
      text: t.precos.feature5,
      included: true
    }, {
      text: t.precos.feature6,
      included: true
    }, {
      text: t.precos.feature7,
      included: true,
      detail: '(3 acessos)'
    }, {
      text: t.precos.feature8,
      included: true,
      detail: 'Avançado / BI'
    }, {
      text: t.precos.feature9,
      included: true,
      detail: '(2 por mês)'
    }, {
      text: t.precos.feature10,
      included: true
    }],
    popular: false
  }];
  const faqs = [{
    q: t.precos.faq1Q,
    a: t.precos.faq1A
  }, {
    q: t.precos.faq2Q,
    a: t.precos.faq2A
  }, {
    q: t.precos.faq3Q,
    a: t.precos.faq3A
  }];
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-842" data-mp="2svmw3kbnj3ix">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-843" data-mp="13fasp3gzv846"></div>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto relative z-10" data-id="element-844" data-mp="yg7otmcr37xs">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-845" data-mp="ye51xb6w9uxa">
          <FadeIn data-id="element-846" data-mp="1m73ot6rd19g4">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center gap-2" data-id="element-847" data-mp="1m6lj7jit5ivj">
              <Store size={16} className="text-brand-yellow" data-id="element-848" data-mp="1d5net04gj9cf" />
              {t.paraNegocio.heroEyebrow}
            </span>
            <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 leading-[1.1] tracking-tight" data-id="element-849" data-mp="1h5ec7mohonpx">
              {t.paraNegocio.heroTitle1}{' '}
              <span className="relative inline-block" data-id="element-850" data-mp="5sll8e3iw3ax">
                <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-851" data-mp="28e7s2jpm1iju">
                  {t.paraNegocio.heroTitle2}
                </span>
                {/* SVG Highlight */}
                <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-852" data-mp="1ym1qqjdgxorz">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-853" data-mp="1kownss91x60y" />
                </svg>
              </span>
            </h1>
            <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-10 leading-relaxed max-w-lg" data-id="element-854" data-mp="3a2i66cp0w5eu">
              {t.paraNegocio.heroDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-855" data-mp="3dm0mtwofzdwq">
              <Link to="/contato" className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-full text-base font-bold transition-all text-center shadow-sm hover:shadow-md flex items-center justify-center gap-2 group" data-id="element-856" data-mp="3djebdlyfke98">
                {t.common.falarComNossoTime}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-857" data-mp="2un4r2gwxqrgs" />
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} className="relative" data-id="element-858" data-mp="33lq8u9ul22gi">
            <div className="absolute inset-0 bg-brand-green/5 dark:bg-brand-green/10 rounded-3xl blur-2xl -z-10 transform scale-110" data-id="element-859" data-mp="38mxeo3d60rkj"></div>
            <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 transform rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-860" data-mp="iwkbxdjj551u">
              <div className="flex items-center gap-4 mb-8 border-b border-gray-100 dark:border-gray-700 pb-6" data-id="element-861" data-mp="1910rpho3hyd0">
                <div className="w-14 h-14 bg-brand-green/10 dark:bg-brand-green/20 rounded-2xl flex items-center justify-center text-brand-green-dark dark:text-brand-green-light" data-id="element-862" data-mp="150guke3iuuqv">
                  <Store size={28} data-id="element-863" data-mp="1dyiqwgcb8z7t" />
                </div>
                <div data-id="element-864" data-mp="1snyexokgiqjt">
                  <h3 className="font-bold text-xl text-brand-charcoal dark:text-white" data-id="element-865" data-mp="1snf6qzklzgiz">
                    {t.paraNegocio.dashTitle}
                  </h3>
                  <p className="text-brand-charcoal/50 dark:text-gray-400 text-sm" data-id="element-866" data-mp="1nrgbqt4rz09l">
                    {t.paraNegocio.dashDesc}
                  </p>
                </div>
              </div>
              <div className="space-y-6" data-id="element-867" data-mp="1wnpae5kxhuuo">
                <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl" data-id="element-868" data-mp="7a2ckiskbhj4">
                  <span className="text-brand-charcoal/70 dark:text-gray-300 font-medium" data-id="element-869" data-mp="2cu4jb14otvl">
                    {t.paraNegocio.dashStat1}
                  </span>
                  <span className="font-bold text-brand-green-dark dark:text-brand-green-light text-lg" data-id="element-870" data-mp="2ixxrz8qcyg2s">
                    +124
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl" data-id="element-871" data-mp="254pzleugdgk1">
                  <span className="text-brand-charcoal/70 dark:text-gray-300 font-medium" data-id="element-872" data-mp="1v8mohjhmi7ky">
                    {t.paraNegocio.dashStat2}
                  </span>
                  <span className="font-bold text-brand-green-dark dark:text-brand-green-light text-lg" data-id="element-873" data-mp="209anarc3fspq">
                    38
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl" data-id="element-874" data-mp="1qal5eyk7689h">
                  <span className="text-brand-charcoal/70 dark:text-gray-300 font-medium" data-id="element-875" data-mp="3gnai5nw30s64">
                    {t.paraNegocio.dashStat3}
                  </span>
                  <span className="font-bold text-brand-green-dark dark:text-brand-green-light text-lg" data-id="element-876" data-mp="283gz2vh3dvlu">
                    12
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why BairrouApp */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300" data-id="element-877" data-mp="234vzmy9nzfyg">
        <div className="max-w-7xl mx-auto" data-id="element-878" data-mp="2qtyoor6mob2z">
          <FadeIn className="text-center mb-20" data-id="element-879" data-mp="2quzzqvnn8xkp">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-880" data-mp="2vtbd3gd5dq7o">
              {t.paraNegocio.whyEyebrow}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-881" data-mp="2lv3wwzqonc6q">
              {t.paraNegocio.whyTitle}
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-882" data-mp="1agqb00fhp6tc">
            {[{
            icon: <Users size={24} data-id="element-883" data-mp="12vwkbj9ojlod" />,
            title: t.paraNegocio.why1Title,
            desc: t.paraNegocio.why1Desc
          }, {
            icon: <TrendingUp size={24} data-id="element-884" data-mp="1cuvgw3h67qv0" />,
            title: t.paraNegocio.why2Title,
            desc: t.paraNegocio.why2Desc
          }, {
            icon: <HeartHandshake size={24} data-id="element-885" data-mp="17wkbc4xws2il" />,
            title: t.paraNegocio.why3Title,
            desc: t.paraNegocio.why3Desc
          }, {
            icon: <CreditCard size={24} data-id="element-886" data-mp="f8fxux93il5c" />,
            title: t.paraNegocio.why4Title,
            desc: t.paraNegocio.why4Desc
          }, {
            icon: <MapPin size={24} data-id="element-887" data-mp="p515lqci2acv" />,
            title: t.paraNegocio.why5Title,
            desc: t.paraNegocio.why5Desc
          }, {
            icon: <Store size={24} data-id="element-888" data-mp="3sm19eezdbyut" />,
            title: t.paraNegocio.why6Title,
            desc: t.paraNegocio.why6Desc
          }].map((feature, i) => <FadeIn key={i} delay={i * 0.1} className="bg-brand-cream/50 dark:bg-gray-900/50 p-8 rounded-3xl border-t-4 border-t-brand-yellow border-x border-b border-gray-100 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-900 hover:shadow-md transition-all duration-300 relative overflow-hidden group" data-id="element-889" data-mp="m2ojzsdh5zfv" data-mpi={i}>
                <div className="absolute top-0 right-0 w-16 h-16 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-bl-full -z-10 group-hover:scale-150 transition-transform duration-500" data-id="element-890" data-mp="37hpst1hab2a"></div>
                <div className="w-12 h-12 bg-white dark:bg-gray-800 text-brand-green-dark dark:text-brand-green-light rounded-xl flex items-center justify-center mb-6 shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-891" data-mp="c5tprlo8il7e">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-892" data-mp="1sk1xwb89crzi">
                  {feature.title}
                </h3>
                <p className="text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-893" data-mp="3d4ym9wt68lg9">
                  {feature.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Digital Presence */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-894" data-mp="2yerbm39eo6q4">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-895" data-mp="2uc4je18tojse">
          <FadeIn className="order-2 lg:order-1 relative flex justify-center lg:justify-start" data-id="element-896" data-mp="2uaba0e6trlgp">
            <div className="absolute inset-0 bg-brand-green/5 dark:bg-brand-green/10 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-897" data-mp="2pc03acbttr2p"></div>
            <div className="flex items-end gap-4 sm:gap-6" data-id="element-898" data-mp="2ffdj7prbr41q">
              <figure className="flex flex-col items-center" data-id="element-899" data-mp="2fh83d1urus4r">
                <PhoneImage src="https://cdn.magicpatterns.com/uploads/hj3uAMogHs4Quuj9n9zp49/Business.png" alt="Página da loja no BairrouApp" className="max-w-[200px] transform -rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-900" data-mp="ilshiqvgc4p6" />
                <figcaption className="mt-4 text-xs font-bold uppercase tracking-wide text-brand-charcoal/60 dark:text-gray-400" data-id="element-901" data-mp="qm89r7b60fcq">
                  Página da loja
                </figcaption>
              </figure>
              <figure className="flex flex-col items-center" data-id="element-902" data-mp="gninz3g5ear7">
                <PhoneImage src="https://cdn.magicpatterns.com/uploads/rpMVVAEghRJjzZymbMkbiw/Fidelity.png" alt="Cartão fidelidade digital no BairrouApp" className="max-w-[200px] transform rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-903" data-mp="2xox6fl31geu" />
                <figcaption className="mt-4 text-xs font-bold uppercase tracking-wide text-brand-charcoal/60 dark:text-gray-400" data-id="element-904" data-mp="3u51in6t00g0k">
                  Cartão fidelidade
                </figcaption>
              </figure>
            </div>
          </FadeIn>
          <FadeIn className="order-1 lg:order-2" data-id="element-905" data-mp="31hrx7zxjbqwg">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-906" data-mp="31ibjfsu7x1ga">
              {t.paraNegocio.presenceEyebrow}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-907" data-mp="3ak1b8zs0i2el">
              {t.paraNegocio.presenceTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-10 leading-relaxed" data-id="element-908" data-mp="3kfcplgzid7lt">
              {t.paraNegocio.presenceDesc}
            </p>
            <ul className="space-y-6" data-id="element-909" data-mp="3t8pqp67q7fiz">
              {[t.paraNegocio.presenceList1, t.paraNegocio.presenceList2, t.paraNegocio.presenceList3, t.paraNegocio.presenceList4].map((item, i) => <li key={i} className="flex items-center gap-4" data-id="element-910" data-mp="6zerf8sj8vcy" data-mpi={i}>
                  <div className="w-8 h-8 rounded-full bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light flex items-center justify-center shrink-0" data-id="element-911" data-mp="23zd6fqm4xg0">
                    <CheckCircle2 size={16} data-id="element-912" data-mp="2zx9g1a2b51cc" />
                  </div>
                  <span className="text-brand-charcoal dark:text-gray-200 font-medium" data-id="element-913" data-mp="34vkpvkslrbuk">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* Democratization: business intelligence + loyalty, no own app */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300 border-y border-gray-100 dark:border-gray-700" data-id="element-914" data-mp="3jjflb8xzvubc">
        <div className="max-w-7xl mx-auto" data-id="element-915" data-mp="3njzk0gy7v5zl">
          <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-916" data-mp="3sl5ae84emh5y">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 flex items-center justify-center gap-2" data-id="element-917" data-mp="1t7syao7p56t1">
              <Sparkles size={16} className="text-brand-yellow" aria-hidden="true" data-id="element-918" data-mp="1y74ffybz6eb5" />
              {t.paraNegocio.democEyebrow}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-919" data-mp="1lujwvxb6pmr0">
              {t.paraNegocio.democTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-920" data-mp="1c0a3ccaiaiep">
              {t.paraNegocio.democDesc}
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-921" data-mp="24o5yvdoca49w">
            {[{
            icon: <Gift size={24} data-id="element-922" data-mp="1zn047ab4c6xq" />,
            title: t.paraNegocio.democCard1Title,
            desc: t.paraNegocio.democCard1Desc
          }, {
            icon: <BarChart3 size={24} data-id="element-923" data-mp="o9e5oyjdcszj" />,
            title: t.paraNegocio.democCard2Title,
            desc: t.paraNegocio.democCard2Desc
          }].map((card, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-924" data-mp="2jcavyaaqc4k4" data-mpi={i}>
                <div className="bg-brand-cream dark:bg-gray-900 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 h-full" data-id="element-925" data-mp="2jahnqqkgxca0">
                  <div className="w-12 h-12 bg-white dark:bg-gray-800 text-brand-green-dark dark:text-brand-green-light rounded-xl flex items-center justify-center mb-6 shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-926" data-mp="2abco1gx67cw9">
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-927" data-mp="3324qgw4twxs1">
                    {card.title}
                  </h3>
                  <p className="text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-928" data-mp="2tb0c9ymz9gy9">
                    {card.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.3} className="text-center mt-14" data-id="element-929" data-mp="3ly4clqb4l73p">
            <p className="font-display text-3xl md:text-4xl text-brand-green dark:text-brand-green-light transform -rotate-1" data-id="element-930" data-mp="3qpcnkqs3t0iz">
              {t.paraNegocio.democHighlight}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-brand-charcoal dark:bg-gray-950 text-white py-24 px-6 relative overflow-hidden transition-colors duration-300" data-id="element-931" data-mp="3w2kq4d14uc4c">
        {/* Organic shape background */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-green/20 dark:bg-brand-green/10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-932" data-mp="8r1wyqni9zv8"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-933" data-mp="3d61khxpjf6cj">
          <FadeIn className="text-center mb-20" data-id="element-934" data-mp="3r4rn1sv4ly8a">
            <span className="text-brand-green font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-935" data-mp="3m4n1vpj9wfbq">
              {t.paraNegocio.stepsEyebrow}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-white mb-6 tracking-tight" data-id="element-936" data-mp="16bxvtv6t0adu">
              {t.paraNegocio.stepsTitle}
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-937" data-mp="21k79x0hljtx0">
            {/* Dotted connector line (desktop only) */}
            <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-0.5 border-t-2 border-dashed border-white/20 dark:border-white/10 z-0" aria-hidden="true" data-id="element-938" data-mp="1wlvwygw093ak"></div>

            {[{
            step: '1',
            title: t.paraNegocio.step1Title,
            desc: t.paraNegocio.step1Desc
          }, {
            step: '2',
            title: t.paraNegocio.step2Title,
            desc: t.paraNegocio.step2Desc
          }, {
            step: '3',
            title: t.paraNegocio.step3Title,
            desc: t.paraNegocio.step3Desc
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className="text-center relative z-10" data-id="element-939" data-mp="2y9er5wb6ngv4" data-mpi={i}>
                <div className="w-16 h-16 bg-brand-yellow text-brand-charcoal font-bold text-2xl rounded-full flex items-center justify-center mx-auto mb-8 shadow-md" data-id="element-940" data-mp="3hwhypg71d3zj">
                  {item.step}
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-941" data-mp="38u8okrfojcmo">{item.title}</h3>
                <p className="text-white/60 leading-relaxed max-w-xs mx-auto" data-id="element-942" data-mp="3cuso21nq6sbz">
                  {item.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Pricing Header Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto text-center relative z-10" data-id="element-943" data-mp="2p6j7pf9a6ws8">
        <FadeIn data-id="element-944" data-mp="2p4oms0y0f4o5">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 block" data-id="element-945" data-mp="2u5cmdb0b0ttz">
            {t.precos.heroEyebrow}
          </span>
          <h2 className="text-4xl md:text-6xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 leading-[1.1] tracking-tight max-w-5xl mx-auto" data-id="element-946" data-mp="2g87auvhtn2rc">
            {t.precos.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-947" data-mp="lxzddna0u4mp">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-5xl md:text-7xl tracking-normal transform -rotate-2 inline-block" data-id="element-948" data-mp="lx7ikeo90axh">
                {t.precos.heroTitle2}
              </span>
              {/* SVG Highlight */}
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-949" data-mp="19ntlxsnb5715">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-950" data-mp="10lu7n83ogh1w" />
              </svg>
            </span>
          </h2>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto" data-id="element-951" data-mp="3gd86stremy4g">
            {t.precos.heroDesc}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white dark:bg-gray-800 p-2 rounded-full shadow-sm border border-gray-200 dark:border-gray-700" data-id="element-952" data-mp="20vv9bnq4ok90">
            <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-3 rounded-full font-bold text-sm transition-all ${billingCycle === 'monthly' ? 'bg-brand-green text-white shadow-md' : 'text-brand-charcoal dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} data-id="element-953" data-mp="29n5b22bazmos">
              {t.precos.monthly}
            </button>
            <button onClick={() => setBillingCycle('annual')} className={`px-6 py-3 rounded-full font-bold text-sm transition-all flex items-center gap-2 ${billingCycle === 'annual' ? 'bg-brand-green text-white shadow-md' : 'text-brand-charcoal dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} data-id="element-954" data-mp="2oitis1ftyrxf">
              {t.precos.annual}
              <span className="text-xs bg-brand-yellow text-brand-charcoal px-2 py-1 rounded-full font-extrabold" data-id="element-955" data-mp="304ylkphziq2">
                {t.precos.annualDiscount}
              </span>
            </button>
          </div>
        </FadeIn>
      </section>

      {/* Pricing Cards */}
      <section className="px-6 py-16 max-w-7xl mx-auto relative z-10" data-id="element-956" data-mp="7yg6vr059l66">
        <div className="grid lg:grid-cols-3 gap-8" data-id="element-957" data-mp="3gc6osxf9bv6w">
          {plans.map((plan, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-958" data-mp="3bedje6ls357o" data-mpi={i}>
              <div className={`relative bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-10 shadow-lg border transition-all duration-300 h-full flex flex-col ${plan.popular ? 'border-brand-green dark:border-brand-green-light border-4 transform lg:-translate-y-4 shadow-2xl' : 'border-gray-200 dark:border-gray-700 hover:shadow-xl'}`} data-id="element-959" data-mp="3bfg765nt0rjd">
                {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-yellow text-brand-charcoal px-6 py-2 rounded-full text-xs font-extrabold tracking-wider shadow-md" data-id="element-960" data-mp="2sn3y702nas09">
                    {plan.badge}
                  </div>}

                <h3 className="text-3xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-3 tracking-tight" data-id="element-961" data-mp="z5g40addo5bq">
                  {plan.name}
                </h3>
                <p className="text-brand-charcoal/60 dark:text-gray-300 mb-8 leading-relaxed text-sm" data-id="element-962" data-mp="1qvt74hjxyrw2">
                  {plan.desc}
                </p>

                <div className="mb-8" data-id="element-963" data-mp="1vudzjusyc79y">
                  <span className="text-5xl font-sans font-extrabold text-brand-green-dark dark:text-brand-green-light tracking-tight" data-id="element-964" data-mp="1hv4tvp2dgvk8">
                    {billingCycle === 'monthly' ? plan.priceM : plan.priceA}
                  </span>
                  <span className="text-brand-charcoal/50 dark:text-gray-400 ml-2 font-medium" data-id="element-965" data-mp="6ey2hdsayc0f">
                    / {billingCycle === 'monthly' ? 'mês' : 'mês (anual)'}
                  </span>
                </div>

                <Link to="/contato" className={`block w-full text-center px-8 py-4 rounded-full font-bold transition-all shadow-sm hover:shadow-md mb-8 ${plan.popular ? 'bg-brand-green hover:bg-brand-green-dark text-white' : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700 text-brand-charcoal dark:text-white border-2 border-brand-green dark:border-brand-green-light'}`} data-id="element-966" data-mp="2d3qcgi67h2bl">
                  {plan.cta}
                </Link>

                <ul className="space-y-4 mb-8 flex-grow" data-id="element-967" data-mp="1pd2pcpnpy64g">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-968" data-mp="1kfuezb8tb6dr" data-mpi={idx}>
                      {feature.included ? <CheckCircle2 className="text-brand-green-dark dark:text-brand-green-light shrink-0 mt-0.5" size={20} data-id="element-969" data-mp="1ggbxkpg79dfo" /> : <X className="text-gray-300 dark:text-gray-600 shrink-0 mt-0.5" size={20} data-id="element-970" data-mp="wl0vgrph4wqf" />}
                      <span className={`text-sm leading-relaxed ${feature.included ? 'text-brand-charcoal dark:text-gray-200 font-medium' : 'text-brand-charcoal/40 dark:text-gray-500'}`} data-id="element-971" data-mp="169siclfogquh">
                        {feature.text}
                        {feature.detail && <span className="text-xs ml-1 text-brand-charcoal/50 dark:text-gray-400" data-id="element-972" data-mp="il99o4spuc1v">
                            {feature.detail}
                          </span>}
                      </span>
                    </li>)}
                </ul>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-973" data-mp="1y0f41v4yiiah">
                  <p className="text-xs text-brand-charcoal/60 dark:text-gray-400 leading-relaxed italic" data-id="element-974" data-mp="22zia9iwoauk1">
                    <strong className="text-brand-green-dark dark:text-brand-green-light font-bold not-italic" data-id="element-975" data-mp="1eoss23vhs3i9">
                      Vantagem:
                    </strong>{' '}
                    {plan.advantage}
                  </p>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 px-6 bg-white dark:bg-gray-800 relative z-10 transition-colors duration-300" data-id="element-976" data-mp="1xdz2z4q1nqti">
        <div className="max-w-4xl mx-auto text-center" data-id="element-977" data-mp="1sfnscrrxdca8">
          <FadeIn data-id="element-978" data-mp="1sh8o2kpz719o">
            <div className="flex justify-center gap-1 mb-8 text-brand-yellow" data-id="element-979" data-mp="26ex8xsk1m8eq">
              {[...Array(5)].map((_, i) => <Star key={i} size={24} fill="currentColor" data-id="element-980" data-mp="22csfattlhbs3" data-mpi={i} />)}
            </div>
            <p className="text-3xl md:text-4xl font-display text-brand-charcoal dark:text-white mb-8 leading-relaxed tracking-normal transform -rotate-1" data-id="element-981" data-mp="c0ctpvehr52q">
              {t.precos.testimonialQuote}
            </p>
            <p className="text-brand-charcoal/60 dark:text-gray-400 font-bold tracking-wide uppercase text-sm" data-id="element-982" data-mp="3u0i7lghu29xq">
              {t.precos.testimonialAuthor}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Feature Comparison Matrix */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-983" data-mp="3k94838pvta4l">
        <FadeIn className="text-center mb-16" data-id="element-984" data-mp="3p875q83j8e2j">
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-985" data-mp="3ac9dv20n5vw4">
            {t.precos.matrixTitle}
          </h2>
          <p className="text-sm text-brand-charcoal/60 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-986" data-mp="31c2rsrqvnnlc">
            {t.precos.matrixNote}
          </p>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-987" data-mp="36bfp66fdkd26">
          <div className="overflow-x-auto" data-id="element-988" data-mp="zkpyz126p65n">
            <table className="w-full bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-989" data-mp="zm14p6f6wnze">
              <thead data-id="element-990" data-mp="26bgueqquvv7y">
                <tr className="bg-brand-cream dark:bg-gray-900" data-id="element-991" data-mp="26bywi40793ns">
                  <th className="text-left p-6 font-bold text-brand-charcoal dark:text-white text-sm uppercase tracking-wider" data-id="element-992" data-mp="269m5z2gdlkz4">
                    Funcionalidade / Recurso
                  </th>
                  <th className="text-center p-6 font-bold text-brand-charcoal dark:text-white text-sm uppercase tracking-wider" data-id="element-993" data-mp="2g7s7vk26mr3z">
                    {t.precos.plan1Name}
                  </th>
                  <th className="text-center p-6 font-bold text-brand-green-dark dark:text-brand-green-light text-sm uppercase tracking-wider bg-brand-green/5 dark:bg-brand-green/10" data-id="element-994" data-mp="2p4164vefbjoj">
                    {t.precos.plan2Name}
                  </th>
                  <th className="text-center p-6 font-bold text-brand-charcoal dark:text-white text-sm uppercase tracking-wider" data-id="element-995" data-mp="33zh7llhc2huy">
                    {t.precos.plan3Name}
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-996" data-mp="19lg8czhvfsuc">
                {[[true, true, true], [true, true, true], [true, true, true], [true, true, true], [false, true, true], [false, true, true], [false, '1 usuário', '3 acessos'], [false, 'Básico', 'Avançado / BI'], [false, false, '2 por mês'], [false, false, true]].map((row, idx) => <tr key={idx} className="border-t border-gray-200 dark:border-gray-700" data-id="element-997" data-mp="9o4rm2e6bux7" data-mpi={idx}>
                    <td className="p-6 text-brand-charcoal dark:text-gray-200 font-medium text-sm" data-id="element-998" data-mp="nq2st203xoy">
                      {[t.precos.feature1, t.precos.feature2, t.precos.feature3, t.precos.feature4, t.precos.feature5, t.precos.feature6, t.precos.feature7, t.precos.feature8, t.precos.feature9, t.precos.feature10][idx]}
                    </td>
                    {row.map((cell, cellIdx) => <td key={cellIdx} className={`p-6 text-center ${cellIdx === 1 ? 'bg-brand-green/5 dark:bg-brand-green/10' : ''}`} data-id="element-999" data-mp="2dteca0wovl1s" data-mpi={cellIdx}>
                        {cell === true ? <CheckCircle2 className="text-brand-green-dark dark:text-brand-green-light inline-block" size={20} data-id="element-1000" data-mp="1q5mystzex1yx" /> : cell === false ? <X className="text-gray-300 dark:text-gray-600 inline-block" size={20} data-id="element-1001" data-mp="1g7z4g55pxwha" /> : <span className="text-xs text-brand-charcoal/70 dark:text-gray-300 font-medium" data-id="element-1002" data-mp="11ml1pj6hvqsi">
                            {cell}
                          </span>}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </FadeIn>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 px-6 bg-white dark:bg-gray-800 relative z-10 transition-colors duration-300" data-id="element-1003" data-mp="1kh00b7ow9hft">
        <div className="max-w-4xl mx-auto" data-id="element-1004" data-mp="1u961n811db7o">
          <FadeIn className="text-center mb-16" data-id="element-1005" data-mp="1u84h8cy4rcda">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-1006" data-mp="1pa2w7mh592z6">
              {t.precos.faqTitle}
            </h2>
          </FadeIn>

          <div className="space-y-4" data-id="element-1007" data-mp="1z88xq2z1g73i">
            {faqs.map((faq, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-1008" data-mp="1z65xkq468xl2" data-mpi={idx}>
                <div className="bg-brand-cream dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-1009" data-mp="3srrba2jxidu0">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full text-left p-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" aria-expanded={openFaq === idx} data-id="element-1010" data-mp="1iu1i3gakriv">
                    <span className="font-bold text-brand-charcoal dark:text-white text-lg pr-4" data-id="element-1011" data-mp="39ugf4qtlg0is">
                      {faq.q}
                    </span>
                    <ChevronDown className={`text-brand-green-dark dark:text-brand-green-light shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} size={24} data-id="element-1012" data-mp="3jpi3vljyj0ku" />
                  </button>
                  {openFaq === idx && <div className="px-6 pb-6 text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-1013" data-mp="1302w6ois8b8l">
                      {faq.a}
                    </div>}
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>
    </div>;
}